import type { ReactNode } from 'react'
import { Button } from '@/components/Button'
import { ContentLayout, ContentMain } from '@/components/ContentLayout'
import {
  EmptyState,
  EmptyStateActions,
  EmptyStateDescription,
  EmptyStateIllustration,
  EmptyStateTitle,
} from '@/components/EmptyState'
import { SearchField } from '@/components/SearchField'
import { Segmented } from '@/components/Segmented'
import { AutoSkeleton } from '@/components/Skeleton'
import {
  StickyViewHeader,
  StickyViewHeaderExtras,
  StickyViewHeaderMain,
} from '@/components/StickyViewHeader'
import type { LegendListFilter } from '@/features/legends/LegendList/LegendListViewModel'
import { pluralize } from '@/utils/pluralization-util'
import styles from './LegendList.module.css'
import CreateJobIllustration from '@/components/SpotIllustration/CreateJobIllustration'

export interface LegendListProps {
  /**
   * The workspace selector shown as the header's heading.
   */
  heading: ReactNode
  canCreateLegends: boolean
  onCreateLegend: () => unknown
  filter: LegendListFilter
  onFilterChange: (filter: LegendListFilter) => unknown
  inactiveCount: number
  searchText: string
  onSearchTextChange: (searchText: string) => unknown
  count: number
  /**
   * Renders the counts as skeletons, while the legends load.
   */
  isSkeleton?: boolean
  /**
   * The legend cards, or their skeletons while they load.
   */
  children?: ReactNode
  /**
   * The new legend dialog.
   */
  dialogs?: ReactNode
  testId?: string
}

/**
 * The legends of a workspace, as cards.
 */
export function LegendList({
  heading,
  canCreateLegends,
  onCreateLegend,
  filter,
  onFilterChange,
  inactiveCount,
  searchText,
  onSearchTextChange,
  count,
  isSkeleton,
  children,
  dialogs,
  testId,
}: LegendListProps) {
  return (
    <div className={styles.root} data-test={testId}>
      <StickyViewHeader bordered>
        <StickyViewHeaderMain>{heading}</StickyViewHeaderMain>
        {canCreateLegends && (
          <StickyViewHeaderExtras>
            <Button
              label="New legend"
              variant="primary"
              iconLeft="add"
              onPress={onCreateLegend}
              testId={testId && `${testId}-new-button`}
            />
          </StickyViewHeaderExtras>
        )}
      </StickyViewHeader>
      <ContentLayout>
        <ContentMain>
          <div className={styles.toolbar}>
            <span className={styles.count}>
              <AutoSkeleton isSkeleton={isSkeleton} characters={10}>
                {count} {pluralize('legend', count)}
              </AutoSkeleton>
            </span>
            <Segmented
              options={[
                {
                  value: 'Active',
                  label: 'Active',
                  testId: testId && `${testId}-filter-active`,
                },
                {
                  value: 'All',
                  label:
                    inactiveCount && !isSkeleton
                      ? `All (${inactiveCount} inactive)`
                      : 'All',
                  testId: testId && `${testId}-filter-all`,
                },
              ]}
              value={filter}
              onChange={onFilterChange}
              aria-label="Legends to show"
            />
            <div className={styles.search}>
              <SearchField
                value={searchText}
                onChange={onSearchTextChange}
                placeholder="Search legends"
                stretch
                testId={testId && `${testId}-search`}
              />
            </div>
          </div>
          {children}
        </ContentMain>
      </ContentLayout>
      {dialogs}
    </div>
  )
}

export interface LegendGridProps {
  children?: ReactNode
}

/**
 * Lays legend cards out in a grid.
 */
export function LegendGrid({ children }: LegendGridProps) {
  return <div className={styles.grid}>{children}</div>
}

export interface LegendListEmptyProps {
  canCreateLegends: boolean
  onCreateLegend: () => unknown
  testId?: string
}

/**
 * Shown when the workspace has no legends yet.
 */
export function LegendListEmpty({
  canCreateLegends,
  onCreateLegend,
  testId,
}: LegendListEmptyProps) {
  return (
    <EmptyState grow testId={testId}>
      <EmptyStateIllustration illustration={CreateJobIllustration} />
      <EmptyStateTitle>Welcome to the Worklayer Legend Builder</EmptyStateTitle>
      <EmptyStateDescription>
        This is where you build your business workflows, also called legends.
        Let's get started with creating your first legend.
      </EmptyStateDescription>
      {canCreateLegends && (
        <EmptyStateActions>
          <Button
            label="Create a new legend"
            variant="primary"
            onPress={onCreateLegend}
            testId={testId && `${testId}-create`}
          />
        </EmptyStateActions>
      )}
    </EmptyState>
  )
}
